import { useState } from "react";
import { Button, Form, Input, App as AntApp } from "antd";
import { CheckOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

import { authApi } from "../api/auth";
import { useAuthStore } from "../store/auth";

function passwordStrength(p: string) {
  if (!p) return 0;
  let score = 0;
  if (p.length >= 10) score += 1;
  if (/\d/.test(p)) score += 1;
  if (/[A-Z]/.test(p)) score += 1;
  if (/[^a-zA-Z0-9]/.test(p)) score += 1;
  return score;
}

export function ChangePassword() {
  const [loading, setLoading] = useState(false);
  const [newPass, setNewPass] = useState("");
  const { setAuth, setMustChangePassword, user } = useAuthStore();
  const navigate = useNavigate();
  const { notification } = AntApp.useApp();

  const score = passwordStrength(newPass);
  const reqs = [
    { label: "At least 10 characters", met: newPass.length >= 10 },
    { label: "Contains a number", met: /\d/.test(newPass) },
    { label: "Contains an uppercase letter", met: /[A-Z]/.test(newPass) },
    { label: "Contains a symbol", met: /[^a-zA-Z0-9]/.test(newPass) },
  ];

  const onFinish = async (values: { current: string; next: string; confirm: string }) => {
    if (values.next !== values.confirm) {
      notification.error({ message: "Passwords do not match" });
      return;
    }
    setLoading(true);
    try {
      const res = await authApi.changePassword(values.current, values.next);
      setAuth({
        user: res.user,
        access: res.access,
        refresh: res.refresh,
        must_change_password: false,
      });
      setMustChangePassword(false);
      notification.success({ message: "Password updated" });
      navigate(user?.role === "superuser" ? "/admins" : "/dashboard", { replace: true });
    } catch (e: unknown) {
      const err = e as { response?: { data?: Record<string, unknown> } };
      const data = err.response?.data;
      notification.error({
        message: typeof data === "object" && data ? JSON.stringify(data) : "Could not update password",
      });
    } finally {
      setLoading(false);
    }
  };

  const strengthLabels = ["Enter a password", "Weak", "Fair", "Good", "Strong"];
  const strengthColors = ["#E2E8F0", "var(--danger)", "var(--warning)", "#84CC16", "var(--success)"];

  return (
    <div className="split-auth">
      <div className="split-auth-left">
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 32, fontWeight: 600 }}>ERPMan</div>
          <div style={{ fontSize: 14, color: "var(--text-muted)", marginTop: 8 }}>Order Management System</div>
          <svg
            width="200"
            height="160"
            viewBox="0 0 200 160"
            style={{ marginTop: 40, stroke: "#CBD5E1", fill: "none", strokeWidth: 1.2 }}
          >
            <rect x="50" y="60" width="100" height="80" rx="4" />
            <path d="M70 60V45a30 30 0 0160 0v15" />
            <circle cx="100" cy="100" r="6" />
            <line x1="100" y1="106" x2="100" y2="120" />
          </svg>
        </div>
      </div>
      <div className="split-auth-right">
        <div className="auth-card">
          <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>Set a new password</h1>
          <div style={{ fontSize: 14, color: "var(--text-secondary)", marginTop: 4 }}>
            You're logging in for the first time. Please choose a secure password before continuing.
          </div>
          <Form layout="vertical" onFinish={onFinish} style={{ marginTop: 24 }} requiredMark={false}>
            <Form.Item label="Current Password" name="current" rules={[{ required: true }]}>
              <Input.Password placeholder="Temp password from admin" size="large" />
            </Form.Item>
            <Form.Item label="New Password" name="next" rules={[{ required: true }]}>
              <Input.Password size="large" onChange={(e) => setNewPass(e.target.value)} />
            </Form.Item>
            <div style={{ display: "flex", gap: 4, marginTop: -4, marginBottom: 6 }}>
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  style={{
                    height: 4,
                    flex: 1,
                    borderRadius: 2,
                    background: i < score ? strengthColors[score] : "#E2E8F0",
                  }}
                />
              ))}
            </div>
            <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{strengthLabels[score]}</div>
            <div style={{ marginTop: 12, marginBottom: 8, display: "flex", flexDirection: "column", gap: 6 }}>
              {reqs.map((r) => (
                <div
                  key={r.label}
                  style={{
                    display: "flex",
                    gap: 8,
                    fontSize: 12,
                    color: r.met ? "var(--success)" : "var(--text-muted)",
                  }}
                >
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: 999,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: r.met ? "var(--success)" : "transparent",
                      border: r.met ? "none" : "1px solid #CBD5E1",
                      color: "white",
                    }}
                  >
                    {r.met && <CheckOutlined style={{ fontSize: 8 }} />}
                  </span>
                  {r.label}
                </div>
              ))}
            </div>
            <Form.Item label="Confirm New Password" name="confirm" rules={[{ required: true }]}>
              <Input.Password size="large" />
            </Form.Item>
            <Button type="primary" htmlType="submit" size="large" block loading={loading}>
              Update Password & Continue
            </Button>
          </Form>
        </div>
      </div>
    </div>
  );
}
